import {
  Button,
  Header,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
} from "date-fns";

import React from "react";
import { parseErrorDetails } from "../../helpers/errorUtils";
import { CanonicalBusinessEvent, ErrorDetails } from "../../types";
import { usePublishStatus } from "../../hooks/usePublishStatus";
import ModalFooter from "../common/ModalFooter";
import ErrorContentRenderer from "./ErrorContentRenderer";
import { getCBEPublishStatus } from "../../helpers/healthUtils";

interface PublishStatusProps {
  cbe: CanonicalBusinessEvent;
}

export default function PublishStatusComponent({ cbe }: PublishStatusProps) {
  const {
    isModalVisible,
    expandAll,
    handleModalOpen,
    handleModalClose,
    handleExpandAll,
    getCbeLinks,
  } = usePublishStatus(cbe);

  function renderSuccessLinks() {
    const cbeLinks = getCbeLinks();
    const publishTime = cbe.publishStatus?.publishTime;
    const formattedTime = publishTime
      ? new Date(publishTime).toLocaleString()
      : null;
    const statusText = formattedTime
      ? `Published at ${formattedTime}`
      : "Publish Success";

    if (cbeLinks.length === 1) {
      return (
        <Link
          key="success-link"
          external
          externalIconAriaLabel="Opens in a new tab"
          href={cbeLinks[0].href}
          data-testid="FlashLink"
        >
          <StatusIndicator type="success">{statusText}</StatusIndicator>
        </Link>
      );
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <StatusIndicator type="success">{statusText}</StatusIndicator>
        {cbeLinks.map((link, index) => (
          <Link
            key={`success-link-${index}`}
            external
            externalIconAriaLabel="Opens in a new tab"
            href={link.href}
            data-testid={`FlashLink-${index}`}
          >
            {link.label}
          </Link>
        ))}
      </SpaceBetween>
    );
  }

  if (cbe.idempotenceId.includes("NONE")) {
    return <StatusIndicator type="info">NoOp</StatusIndicator>;
  }

  const publishStatus = getCBEPublishStatus(cbe);

  if (publishStatus.isUnpublished) {
    const completionDate = cbe.queueBurndown
      ? new Date(cbe.queueBurndown.estimatedCompletionTime)
      : null;

    const numberFormatter = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 0,
    });
    return (
      <StatusIndicator type="pending">
        Status Unknown/Pending Publish{" "}
        {completionDate ? (
          <Popover
            dismissButton={false}
            fixedWidth={false}
            position="bottom"
            triggerType="custom"
            content={
              <>
                Expected to be published{" "}
                <strong>
                  {formatDistanceToNow(
                    new Date(
                      Math.max(completionDate.getTime(), Date.now() + 1000)
                    ),
                    { addSuffix: true }
                  )}
                </strong>{" "}
                at {completionDate.toLocaleString()}
                <br />
                <strong>Oldest item in queue:</strong>{" "}
                {formatDuration(
                  intervalToDuration({
                    start: 0,
                    end: cbe.queueBurndown!.queueMaxAgeSeconds * 1000,
                  })
                )}
                <br />
                <strong>Number of items in queue:</strong>{" "}
                {numberFormatter.format(cbe.queueBurndown!.queueDepth)}
                <br />
                <strong>Processing rate:</strong>{" "}
                {numberFormatter.format(cbe.queueBurndown!.queueProcessingRate)}
                /s
              </>
            }
          >
            <Button
              iconName="status-info"
              variant="icon"
              ariaLabel="View info"
            />
          </Popover>
        ) : null}
      </StatusIndicator>
    );
  }

  if (publishStatus.isSuccess) {
    return renderSuccessLinks();
  }

  const errorDetails: ErrorDetails = parseErrorDetails(
    cbe.publishStatus?.stackTrace || "No error details",
    cbe.destination,
    cbe
  );

  return (
    <>
      <Button
        onClick={handleModalOpen}
        variant="inline-link"
        className="publish-status-button"
      >
        <StatusIndicator type="error">Error Publishing</StatusIndicator>
      </Button>
      <Modal
        className="error-modal"
        onDismiss={handleModalClose}
        visible={isModalVisible}
        closeAriaLabel="Close modal"
        size="max"
        footer={
          <ModalFooter
            stackTrace={errorDetails.stackTrace}
            inputRequestUrl={errorDetails.inputRequestUrl}
            onClose={handleModalClose}
            onExpandToggle={handleExpandAll}
            expandAll={expandAll}
          />
        }
        header={
          <Header
            variant="h2"
            description={
              cbe.publishStatus?.publishTime
                ? `Publish attempted at: ${new Date(
                    cbe.publishStatus.publishTime
                  ).toLocaleString()}`
                : undefined
            }
          >
            Error Type: {errorDetails.errorType || "Unknown"}
          </Header>
        }
      >
        <ErrorContentRenderer
          errorDetails={errorDetails}
          isModalVisible={isModalVisible}
          expandAll={expandAll}
        />
      </Modal>
    </>
  );
}
